import React, { useContext, useEffect, useState } from 'react';
import {
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate
} from 'react-router-dom';
import is from 'is_js';
import MainLayout from './MainLayout';
import ErrorLayout from './ErrorLayout';
import Landing from 'components/pages/landing/Landing';
import { toast, ToastContainer } from 'react-toastify';
import { CloseButton } from 'components/common/Toast';
import Error404 from 'components/errors/Error404';
import Error500 from 'components/errors/Error500';
import { useAuth } from 'hooks/useAuth';
import { isEmpty, get, forIn } from 'lodash';
import AppContext from 'context/Context';
import { getTenantFromToken } from 'utils/routeUtils';
import Loader from 'components/common/Loader';

// Import components
import Dashboard from 'components/dashboards/default';
import Monitoring from 'components/dashboards/monitoring';
import Comparison from 'components/dashboards/comparison';
import TableProject from 'components/dashboards/table-data';
import FarmLevel from 'components/dashboards/farm-level';
import FarmLevelReport from 'components/dashboards/farmlevelReport';
import DynamicReport from 'screens/common/DynamicReport/index';
import DynamicCollapseReport from 'screens/common/DynamicCollapseReport/index';
import WeatherDashboard from 'screens/WeatherDashboard';
import DashboardAnalytics from 'screens/DashboardAnalytics';
import SmsDashboard from 'screens/SmsDashboard';
import SmsManagement from 'screens/SmsManagement';
import { CropInfectionRulesReport } from 'screens/CropInfection/CropInfectionRulesReport';
import CropInfectionRuleCreation from 'screens/CropInfection/CropInfectionRuleCreation';
import ErrorSelectCluster from 'components/errors/ErrorSelectCluster';
import ManageProjects from 'components/dashboards/manage-projects';
import NewProject from 'components/dashboards/manage-projects/NewProject';
import EditProject from 'components/dashboards/manage-projects/EditProject';

// API hooks
import useApi from 'hooks/useApi';
import { getMonitoringLocations } from 'api/endpoints/TerrestrialServices';
import { getBlockList } from 'api/endpoints/ManagementServices';

// Import components for farmers and cultivation
import FarmerSms from 'screens/FarmerReport/FarmerSms/index.jsx';
import CreateFarm from 'screens/FarmerReport/CreateFarm';
import CreateFarmer from 'screens/FarmerReport/CreateFarmer';
import EditFarmer from 'screens/FarmerReport/EditFarmer';
import EditFarm from 'screens/FarmerReport/EditFarm';
import CultivationForm from 'screens/Cultivation/Cultivationform';
import HarvestForm from 'screens/Cultivation/HarvestForm';
import FarmerGroupForm from 'screens/FarmerGroup/index';
import CropCalendar from 'screens/CropInfection/CropCalendar';
import FieldOfficerForm from 'screens/FieldOfficer/FieldOfficerForm';
import CreateMarketPrice from 'screens/MarketPrice';

// Report config
import reportprobs from 'screens/common/DynamicReport/reportprobs';
import ProcessingCenter from 'screens/ProcessingCenter';
import OfflineData from 'screens/OfflineData';
import DynamicFormPage from 'screens/Traceability/DynamicFormPage';
import formProps from 'screens/Traceability/formProps';
import Environmental_indicators from 'components/dashboards/environmental_indicators';
import TraceabilityDashboard from 'screens/TraciblityDashboard';
import { getEnvProjList } from 'api/endpoints/Enviormental_Indicators';

const Layout = () => {
  const HTMLClassList = document.getElementsByTagName('html')[0].classList;
  const {
    config: { navbarPosition },
    setConfig
  } = useContext(AppContext);

  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isMonitoring = pathname.includes('monitor');
  const isPlotOverlay = pathname.includes('plotoverlay');
  const isHabitatMonitoring = pathname.includes('habitat');

  const tenant = getTenantFromToken();
  const [monitoringLocationData, setMonitoringLocationData] = useState([]);
  const [firstMonitoringCluster, setFirstMonitoringCluster] = useState('');
  const [firstPlotOverlayCluster, setFirstPlotOverlayCluster] = useState('');
  const [firstHabitatCluster, setFirstHabitatCluster] = useState('');

  // Get API hooks
  const getMonitoringLocationAPI = useApi(getMonitoringLocations);
  const getBlockListAPI = useApi(getBlockList);
  const getHabitatMonitoringAPI = useApi(getEnvProjList);
  const apiHooks = [
    getMonitoringLocationAPI,
    getBlockListAPI,
    getHabitatMonitoringAPI
  ];

  const performSequentialAPICalls = async () => {
    for (let i = 0; i < apiHooks.length; i++) {
      await apiHooks[i].request(tenant);
    }
  };

  useEffect(() => {
    performSequentialAPICalls();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      // Handle monitoring locations
      if (!isEmpty(getMonitoringLocationAPI.data)) {
        const responseData = get(getMonitoringLocationAPI.data, 'results', {});
        const filterOptions = [];
        forIn(responseData, (value, key) => {
          filterOptions.push({ key, value });
        });
        if (!isEmpty(filterOptions)) {
          const firstClusterKey = filterOptions[0]?.key;
          setFirstMonitoringCluster(firstClusterKey);
          setMonitoringLocationData(filterOptions);

          // Store monitoring data in localStorage for use in getDefaultRoute
          localStorage.setItem(
            'monitoring_location_data',
            JSON.stringify(filterOptions)
          );
          localStorage.setItem('first_monitoring_cluster', firstClusterKey);

          isMonitoring && setConfig('cluster', firstClusterKey);
        }
      }

      // Handle plot overlay (block list) locations
      if (!isEmpty(getBlockListAPI.data)) {
        const responseData = get(getBlockListAPI.data, 'results', {});
        const filterOptions = [];
        forIn(responseData, block => {
          filterOptions.push({ key: block.id, value: block.name });
        });
        if (!isEmpty(filterOptions)) {
          const firstClusterKey = filterOptions[0]?.key;
          setFirstPlotOverlayCluster(firstClusterKey);

          // Store plot overlay data separately
          localStorage.setItem(
            'plotoverlay_location_data',
            JSON.stringify(filterOptions)
          );
          localStorage.setItem('first_plotoverlay_cluster', firstClusterKey);

          isPlotOverlay && setConfig('cluster', firstClusterKey);
        }
      }

      // Handle habitat monitoring locations
      if (!isEmpty(getHabitatMonitoringAPI.data)) {
        const responseData = get(getHabitatMonitoringAPI.data, 'results', {});
        const filterOptions = [];
        forIn(responseData, block => {
          filterOptions.push({ key: block.id, value: block.name });
        });
        if (!isEmpty(filterOptions)) {
          const firstClusterKey = filterOptions[0]?.key;
          setFirstHabitatCluster(firstClusterKey);

          // Store habitat data separately
          localStorage.setItem(
            'habitat_location_data',
            JSON.stringify(filterOptions)
          );
          localStorage.setItem('first_habitat_cluster', firstClusterKey);

          isHabitatMonitoring && setConfig('cluster', firstClusterKey);
        }
      }
    };

    fetchData();
  }, [
    getMonitoringLocationAPI.data,
    getBlockListAPI.data,
    getHabitatMonitoringAPI.data,
    isMonitoring,
    isPlotOverlay,
    isHabitatMonitoring
  ]);

  useEffect(() => {
    // Extract the tenant from the pathname using a regular expression
    const uuidPattern = /[0-9a-f]{32}/i;
    const match = uuidPattern.exec(pathname);

    if (match) {
      const newTenant = match[0];
      if (tenant !== newTenant) {
        setConfig('tenant', newTenant);
      }
    }

    // Skip if no tenant is available yet
    if (!tenant) return;

    // Parse the pathname to identify if it's a monitor or plotoverlay route that needs a cluster
    const pathSegments = pathname.split('/').filter(Boolean);

    // Check if this is a tenant path followed by monitor, plotoverlay, or habitat without a cluster
    const isTenantPath = pathSegments[0] === tenant;
    const needsCluster =
      isTenantPath &&
      (pathSegments[1] === 'monitor' ||
        pathSegments[1] === 'plotoverlay' ||
        pathSegments[1] === 'habitat') &&
      pathSegments.length === 2; // Only tenant/monitor or tenant/plotoverlay, no cluster specified

    if (needsCluster) {
      // Always use the first cluster for the specific route type
      let clusterToUse = '';

      if (pathSegments[1] === 'monitor') {
        clusterToUse = firstMonitoringCluster;
      } else if (pathSegments[1] === 'plotoverlay') {
        clusterToUse = firstPlotOverlayCluster;
      } else if (pathSegments[1] === 'habitat') {
        clusterToUse = firstHabitatCluster;
      }

      // Only redirect if we have a cluster to use
      if (clusterToUse) {
        const newPath = `/${tenant}/${pathSegments[1]}/${clusterToUse}`;
        navigate(newPath, { replace: true });
        return; // Exit early after redirect
      }
    }
  }, [
    pathname,
    tenant,
    firstMonitoringCluster,
    firstPlotOverlayCluster,
    firstHabitatCluster,
    navigate
  ]);

  useEffect(() => {
    // Browser detection
    if (is.windows()) {
      HTMLClassList.add('windows');
    }
    if (is.chrome()) {
      HTMLClassList.add('chrome');
    }
    if (is.firefox()) {
      HTMLClassList.add('firefox');
    }
    if (is.safari()) {
      HTMLClassList.add('safari');
    }
  }, [HTMLClassList]);

  useEffect(() => {
    if (navbarPosition === 'double-top') {
      HTMLClassList.add('double-top-nav-layout');
    }
    return () => HTMLClassList.remove('double-top-nav-layout');
  }, [navbarPosition]);

  // Check if the current domain is envira.farmsio.com
  const isDemoDomain = window.location.hostname === 'envira.farmsio.com';
  if (!tenant) {
    return <Loader />;
  }

  return (
    <>
      <Routes>
        <Route path="landing" element={<Landing />} />
        <Route element={<ErrorLayout />}>
          <Route path="errors/404" element={<Error404 />} />
          <Route path="errors/500" element={<Error500 />} />
        </Route>

        {/* Main Layout with nested routes */}
        <Route path="/" element={<MainLayout />}>
          <Route path={tenant}>
            {/* Dashboard routes */}
            <Route path="evaluate" element={<ErrorSelectCluster />} />
            <Route path="monitor" element={<ErrorSelectCluster />} />
            <Route path="evaluate/:cluster" element={<Dashboard />} />
            <Route path="monitor/:cluster" element={<Monitoring />} />
            <Route path="comparison/" element={<Comparison />} />
            <Route path="village/" element={<TableProject />} />
            <Route path="plotoverlay" element={<ErrorSelectCluster />} />
            <Route path="habitat" element={<ErrorSelectCluster />} />
            <Route path="plotoverlay/:cluster" element={<FarmLevel />} />
            <Route
              path="habitat/:cluster"
              element={<Environmental_indicators />}
            />
            <Route path="farmlevelReport/" element={<FarmLevelReport />} />
            <Route
              path="dashboardAnalytics/"
              element={<DashboardAnalytics />}
            />
            <Route path="sms-dashboard/" element={<SmsDashboard />} />
            <Route path="weatherReport/" element={<WeatherDashboard />} />
            <Route
              path="traceabilityDashboard/"
              element={<TraceabilityDashboard />}
            />
            <Route
              path="habitat/:cluster"
              element={<Environmental_indicators />}
            />

            {/* Project management routes */}
            <Route path="projects" element={<ManageProjects />} />
            {!isDemoDomain && (
              <>
                <Route path="projects/new" element={<NewProject />} />
                <Route path="projects/:request_id" element={<EditProject />} />
                <Route
                  path="projects/:request_id/edit"
                  element={<EditProject />}
                />
              </>
            )}

            {/* Dynamic report routes */}
            <Route
              path="farmerReport/"
              element={
                <DynamicReport
                  key="farmerReport"
                  {...reportprobs('farmerReport')}
                />
              }
            />
            <Route
              path="farmlandReport/"
              element={
                <DynamicReport
                  key="farmlandReport"
                  {...reportprobs('farmland')}
                />
              }
            />
            <Route
              path="farmerGroupReport/"
              element={
                <DynamicReport
                  key="farmerGroupReport"
                  {...reportprobs('farmerGroupReport')}
                />
              }
            />
            <Route
              path="cultivationReport"
              element={
                <DynamicReport
                  key="cultivationReport"
                  {...reportprobs('cultivationReport')}
                />
              }
            />
            <Route
              path="smslogReport"
              element={
                <DynamicReport
                  key="smslogReport"
                  {...reportprobs('smslogReport')}
                />
              }
            />
            <Route
              path="perilAlerts/"
              element={
                <DynamicReport
                  key="perilAlerts"
                  {...reportprobs('perilAlerts')}
                />
              }
            />
            <Route
              path="fieldOfficerReport/"
              element={
                <DynamicReport
                  key="fieldOfficerReport"
                  {...reportprobs('fieldOfficerReport')}
                />
              }
            />

            {/* Dynamic collapse report routes */}
            <Route
              path="cropcalendarRuleReport"
              element={
                <DynamicCollapseReport
                  key="cropcalendarRuleReport"
                  {...reportprobs('cropcalendarRuleReport')}
                />
              }
            />
            <Route
              path="cropInfection/report"
              element={
                <DynamicCollapseReport
                  key="cropInfectionReport"
                  {...reportprobs('cropInfectionReport')}
                />
              }
            />
            <Route
              path="crop-calendar-report/"
              element={
                <DynamicCollapseReport
                  key="cropCalendarAlert"
                  {...reportprobs('cropCalendarAlert')}
                />
              }
            />
            <Route
              path="procurementReport/"
              element={
                <DynamicCollapseReport
                  key="procurementReport"
                  {...reportprobs('procurementReport')}
                />
              }
            />
            <Route
              path="marketpriceReport/"
              element={
                <DynamicCollapseReport
                  key="marketpriceReport"
                  {...reportprobs('marketpriceReport')}
                />
              }
            />
            <Route
              path="centerList/"
              element={
                <DynamicReport
                  key="centerList"
                  {...reportprobs('centerList')}
                />
              }
            />
            <Route
              path="transferReport"
              element={
                <DynamicCollapseReport
                  key="transferReport"
                  {...reportprobs('transferReport')}
                />
              }
            />
            <Route
              path="receptionReport/"
              element={
                <DynamicReport
                  key="receptionReport"
                  {...reportprobs('receptionReport')}
                />
              }
            />
            <Route
              path="processReport/"
              element={
                <DynamicReport
                  key="processReport"
                  {...reportprobs('processReport')}
                />
              }
            />
            {/* Feature routes */}
            <Route path="sms-management/" element={<SmsManagement />} />
            <Route
              path="cropInfection/rules/report"
              element={<CropInfectionRulesReport />}
            />
            <Route
              path="cropInfection/rules/create"
              element={<CropInfectionRuleCreation />}
            />

            {/* Form routes */}
            <Route path="farmerReport/farmersms" element={<FarmerSms />} />
            <Route
              path="farmerReport/farmercreate"
              element={<CreateFarmer />}
            />
            <Route
              path="farmerReport/farmeredit/:id"
              element={<EditFarmer />}
            />
            <Route
              path="farmerReport/create_farm/:id"
              element={<CreateFarm />}
            />
            <Route
              path="farmerReport/farmlandedit/:id"
              element={<EditFarm />}
            />
            <Route
              path="addCultivation/:id"
              element={<CultivationForm edit={false} />}
            />
            <Route
              path="cultivationReport/edit/:id"
              element={<CultivationForm edit={true} />}
            />
            <Route
              path="cultivation/view/:id"
              element={<CultivationForm edit={false} view={true} />}
            />
            <Route path="cultivation/harvest/:id" element={<HarvestForm />} />
            <Route
              path="farmerGroup"
              element={<FarmerGroupForm edit={false} />}
            />
            <Route
              path="farmerGroupReport/edit/:groupId"
              element={<FarmerGroupForm edit={true} />}
            />
            <Route path="cropcalendar/:id?" element={<CropCalendar />} />
            <Route
              path="procurement/:id?"
              element={
                <DynamicFormPage
                  key="procurement"
                  {...formProps('procurement')}
                />
              }
            />
            <Route
              path="reception/:id?"
              element={
                <DynamicFormPage key="reception" {...formProps('reception')} />
              }
            />
            <Route
              path="process/:id?"
              element={
                <DynamicFormPage key="process" {...formProps('process')} />
              }
            />
            <Route path="marketprice/:id?" element={<CreateMarketPrice />} />
            <Route
              path="processingCenter/:id?"
              element={<ProcessingCenter />}
            />
            <Route
              path="transfer/:id?"
              element={
                <DynamicFormPage key="transfer" {...formProps('transfer')} />
              }
            />
            <Route
              path="fieldOfficerReport/fieldOfficerCreate"
              element={<FieldOfficerForm edit={false} />}
            />
            <Route
              path="fieldOfficerReport/fieldOfficerEdit/:id"
              element={<FieldOfficerForm edit={true} />}
            />
            <Route path="offline/data" element={<OfflineData />} />
          </Route>

          {/* Exports page (outside tenant path) */}
          <Route
            path="ExportReportsPage/"
            element={
              <DynamicReport
                key="ExportReportsPage"
                {...reportprobs('ExportReportsPage')}
              />
            }
          />
        </Route>

        {/* Catch-all for 404 */}
        <Route path="*" element={<Navigate to="/errors/404" replace />} />
      </Routes>

      <ToastContainer
        closeButton={CloseButton}
        icon={false}
        position={toast.POSITION.BOTTOM_RIGHT}
      />
    </>
  );
};

export default Layout;
